import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { ComplaintCategoryMasterVo, ComplaintCategoryVo, ComplaintConfirmationVo, ComplaintRequest, ComplaintService, CreateComplaintDetail, ResidentLocationVo } from '../../../core/services/complaint.service';
import { AuthContext } from '../../../auth/services/auth.context';
import { LookupItem } from '../../../core/services/lookup.service';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { UserManagementService } from '../../user-management/user-management.service';

@Component({
  selector: 'app-create-my-complaint',
  standalone: true,
  imports: [
    MatCardModule,
    MatTableModule,
    MatCheckboxModule,
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSelectModule,
    MatExpansionModule,
    MatIconModule,
    FormsModule
  ],
  templateUrl: './create-my-complaint.component.html',
  styleUrl: './create-my-complaint.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateMyComplaintComponent implements OnInit, AfterViewInit {

  private snackBar = inject(MatSnackBar);

  complaintForm: FormGroup;
  preferredDayType: LookupItem[] = [];
  preferredDayValue: string = '';
  preferredTimeType: LookupItem[] = [];
  preferredTimeValue: string = '';
  additionalInstructions: string = '';
  dataSource = new MatTableDataSource<ResidentLocationVo>([]);
  selection = new SelectionModel<ResidentLocationVo>(false, []);
  displayedColumns: string[] = ['areaName', 'locationName', 'locationCode', 'residentName', 'select'];
  locationSelected: boolean = false;

  categoryNameOptions: ComplaintCategoryVo[] = [];
  masterComplaintDescriptionOptionsMap: { [key: number]: ComplaintCategoryMasterVo[] } = {};

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  complaintSuccess: boolean = false;
  complaintConfirmations: ComplaintConfirmationVo[] = [];

  showAreasDropDown: boolean = false;
  areas: SimpleCodeValue[] = [];
  areaId!: number;
  showLocations: boolean = true;

  constructor(
    private complaintService: ComplaintService,
    private authContext: AuthContext,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private userManagementService: UserManagementService,
  ) {
    this.complaintForm = this.fb.group({
      complaints: this.fb.array([]) // Initialize FormArray
    });
  }

  ngOnInit(): void {
    const lookups = this.authContext.getLookups();
    this.preferredDayType = lookups['PREFFERED_DAY_TYPE'] || [];
    this.preferredTimeType = lookups['PREFFERED_TIME_TYPE'] || [];
    this.preferredDayValue = 'ANY_DAY';
    this.preferredTimeValue = 'ANY_TIME';
    this.getComplaintCategoryNames();
    if(this.authContext.getUserInfo().userType === 'ROLE_USER') {
      this.showAreasDropDown = false;
      this.showLocations = true;
      this.getUserAllocatedLocations();
    } else {
      this.showAreasDropDown = true;
      this.showLocations = false;
      this.getUserZoneAreas();
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.cdr.detectChanges();
  }

  getUserZoneAreas() {
    this.userManagementService.getUserAreas().subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  onAreaChange() {
    this.showLocations = true;
    this.locationSelected = false;
    this.complaintService.getAllocatedLocationsForAreaId(this.areaId).subscribe(locations => {
      this.dataSource.data = locations;
      this.cdr.detectChanges();
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  backToHome() {
    this.router.navigate(['/app/home'], { replaceUrl: true });
  }

  get complaints(): FormArray {
    return this.complaintForm.get('complaints') as FormArray;
  }

  addComplaint() {
    const complaintGroup = this.getComplaintGroup(this.complaints.length);
    this.complaints.push(complaintGroup);
  }

  removeComplaint(index: number): void {
    this.complaints.removeAt(index);
  }

  getComplaintGroup(i: number): FormGroup {
    return this.fb.group({
      categoryName: [null],
      categoryId: [null], // To store categoryId
      masterComplaintDescription: [null],
      masterCategoryId: [null], // To store masterCategoryId
      comment: [null],
      priorityId: [null],
      complaintMode: ['WEB']
    });
  }

  getComplaintCategoryControl(index: number): FormControl {
    return this.complaints.at(index).get('categoryName') as FormControl;
  }

  getMasterComplaintControl(index: number): FormControl {
    return this.complaints.at(index).get('masterComplaintDescription') as FormControl;
  }

  getCommentControl(index: number): FormControl {
    return this.complaints.at(index).get('comment') as FormControl;
  }

  onCheckboxChange(location: ResidentLocationVo) {
    if (this.selection.isSelected(location)) {
      this.selection.deselect(location);
    } else {
      this.selection.select(location);
    }
    this.locationSelected = this.selection.selected.length > 0;
  }

  getUserAllocatedLocations() {
    this.complaintService.getUserAllocatedLocations().subscribe(locations => {
      this.dataSource.data = locations;
      this.cdr.detectChanges();
    });
  }

  getComplaintCategoryNames() {
    this.complaintService.getUserComplaintCategory().subscribe(categories => {
      this.categoryNameOptions = categories;
      this.cdr.detectChanges();
    });
  }

  onCategorySelected(selectedCategory: string, index: number) {
    const selectedCategoryDetails = this.categoryNameOptions.find(category => category.categoryName === selectedCategory);
    if (selectedCategoryDetails) {

      // Set categoryId in the form
      this.complaints.at(index).get('categoryId')?.setValue(selectedCategoryDetails.categoryId);
      this.complaints.at(index).get('categoryName')?.setValue(selectedCategoryDetails.categoryName);

      this.complaintService.getMasterComplaintCategories(selectedCategoryDetails.categoryId).subscribe(masterComplaints => {
        this.masterComplaintDescriptionOptionsMap[index] = masterComplaints;
        this.cdr.detectChanges();
      });
    }
  }

  onMasterCategorySelected(selectedMasterCategory: string, index: number) {
    const isDuplicate = this.complaints.controls.some((group, i) => {
      return i !== index && group.get('masterComplaintDescription')?.value === selectedMasterCategory;
    });

    if (isDuplicate) {
      this.openSnackBar('This master complaint description has already been selected. Please choose a different one.', 'Okay');
      this.getMasterComplaintControl(index).setValue(null); // Reset the current selection
      return;
    }

    const selectedMasterComplaintDetails = this.masterComplaintDescriptionOptionsMap[index]?.find(
      master => master.complaintDescription === selectedMasterCategory
    );
    
    if (selectedMasterComplaintDetails) {
      // Set masterCategoryId in the form
      this.complaints.at(index).get('masterCategoryId')?.setValue(selectedMasterComplaintDetails.masterComplaintId);
      this.complaints.at(index).get('priorityId')?.setValue(selectedMasterComplaintDetails.priority);
    }
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 30 * 1000 }); // 3 seconds
  }

  submitComplaint() {
    if (this.complaintForm.valid) {
      console.log("valid form");
      // console.log(this.selection.selected);
      const complaintForm = this.complaintForm.value;

      let complaintDetails: CreateComplaintDetail[] = complaintForm.complaints;
      let createComplaint: ComplaintRequest = {
        location: this.selection.selected[0],
        complaintDetails: complaintDetails,
        preferredDay: this.preferredDayValue,
        preferredTime: this.preferredTimeValue,
        complaintNotes: this.additionalInstructions,
      };

      // console.log(createComplaint);
      this.complaintService.createComplaint(createComplaint).subscribe({
        next: (response) => {
          if (response) {
            // console.log(response);
            this.complaintConfirmations = response;
            this.complaintSuccess = true;
            this.cdr.detectChanges(); // Trigger change detection manually
          }
        },
        error: (error) => {
          console.error('POST error:', error);
        },
      });
    }
  }

  bookAnotherComplaint() {
    // First navigate to 'app/home'
    this.router.navigate(['/app/home']).then(() => {
      // Then navigate back to 'app/create-complaint'
      this.router.navigate(['/app/create-my-complaint']);
    });
  }

}